'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { bannerApi } from '@/lib/api';
import type { BannerData } from '@/lib/api';
import { useTranslations } from 'next-intl';

interface CatalogBannerCarouselProps {
  autoPlayMs?: number;
  className?: string;
}

/**
 * Compact banner carousel for catalog pages — fetches CATALOG_TOP banners client-side.
 * Shows nothing while loading or if there are no active banners.
 */
export function CatalogBannerCarousel({ autoPlayMs = 5000, className }: CatalogBannerCarouselProps) {
  const t = useTranslations('common');
  const [banners, setBanners] = useState<BannerData[]>([]);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    bannerApi.getActive('CATALOG_TOP').then(setBanners).catch(() => {});
  }, []);

  const prev = useCallback(
    () => setIndex((i) => (i - 1 + banners.length) % banners.length),
    [banners.length],
  );
  const next = useCallback(
    () => setIndex((i) => (i + 1) % banners.length),
    [banners.length],
  );

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(next, autoPlayMs);
    return () => clearInterval(t);
  }, [next, autoPlayMs, banners.length]);

  if (banners.length === 0) return null;

  const banner = banners[index];
  const color = banner.textColor ?? '#ffffff';

  const bgStyle: React.CSSProperties = banner.imageUrl
    ? {
        backgroundImage: `url(${banner.imageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }
    : banner.bgGradient
    ? { background: banner.bgGradient }
    : { backgroundColor: banner.bgColor ?? '#0f766e' };

  return (
    <div
      className={`relative w-full overflow-hidden mb-2 sm:mb-4${className ? ` ${className}` : ''}`}
      style={{ ...bgStyle, minHeight: '120px' }}
    >
      {banner.imageUrl && !banner.imageOnly && <div className="absolute inset-0 bg-black/40" />}

      {!banner.imageOnly && (
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-8 py-7">
          <h3 className="text-base md:text-lg font-extrabold leading-tight max-w-2xl" style={{ color }}>
            {banner.title}
          </h3>
          {banner.subtitle && (
            <p className="mt-1.5 text-sm max-w-xl opacity-90" style={{ color }}>
              {banner.subtitle}
            </p>
          )}
          {banner.ctaText && banner.ctaUrl && (
            <Link
              href={banner.ctaUrl}
              className="mt-4 inline-flex items-center gap-2 px-5 py-1.5 rounded-full font-semibold text-sm transition-all hover:opacity-90 active:scale-95"
              style={{ backgroundColor: color, color: banner.bgColor ?? '#0f766e' }}
            >
              {banner.ctaText}
            </Link>
          )}
        </div>
      )}

      {banners.length > 1 && (
        <>
          <button
            onClick={prev}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            aria-label={t('prevBanner')}
          >
            <ChevronLeft className="w-4 h-4 text-white" />
          </button>
          <button
            onClick={next}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-1.5 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
            aria-label={t('nextBanner')}
          >
            <ChevronRight className="w-4 h-4 text-white" />
          </button>

          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => setIndex(i)}
                className={`rounded-full transition-all ${
                  i === index ? 'w-4 h-1.5 bg-white' : 'w-1.5 h-1.5 bg-white/50'
                }`}
                aria-label={t('goBanner', { n: i + 1 })}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
